import React, { PropsWithChildren } from 'react';
import { Property } from 'csstype';
import styled from '../../themes/themed-styled-components';
import { times, constant } from 'lodash';

type LayoutProps = PropsWithChildren<{
    header?: JSX.Element | null,
    footer?: JSX.Element | null,
    left?: JSX.Element | null,
    right?: JSX.Element | null
}>;

export const GridLayoutWrap = styled.div<{ $areas: Property.GridArea[][], $columns: Property.GridColumn[], $rows: Property.GridRow[] }>`
width: 100%;
height: 100%;
flex-direction: column;
overflow: hidden;

/* grid container settings */
display: grid;
grid-template-columns: ${({ $columns }) => $columns.join(' ')};
grid-template-rows: ${({ $rows }) => $rows.join(' ')};
grid-template-areas:
    ${({ $areas }) => $areas.map((r) => `"${r.join(' ')}"`).join('\n')};

@media print {
    height: auto;
}
`;

export const GridLayoutElement = styled.div<{ $area: Property.GridArea }>`
    grid-area: ${ (props) => props.$area};
    min-height: 0;
`;

export const GridLayoutContent = styled(GridLayoutElement).attrs({ $area: 'content' })`
    overflow: auto;
`;

const GridLayout = ({ children = null, header = null, footer = null, left = null, right = null }: LayoutProps) => {
    const elements = [<GridLayoutContent key="content">{children}</GridLayoutContent>];
    const crow = ['content'];
    const areas = [crow];
    const rows = ['1fr'];
    const columns = ['1fr'];

    if (left !== null) {
        crow.unshift('left');
        columns.unshift('minmax(5%,auto)');
        elements.unshift(<GridLayoutElement key="left" $area="left">{left}</GridLayoutElement>);
    }

    if (right !== null) {
        crow.push('right');
        columns.push('minmax(5%, auto)');
        elements.push(<GridLayoutElement key="right" $area="right">{right}</GridLayoutElement>);
    }

    const w = crow.length;

    if (header !== null) {
        areas.unshift(times(w, constant('header')));
        rows.unshift('auto');
        elements.unshift(<GridLayoutElement key="header" $area="header">{header}</GridLayoutElement>);
    }

    if (footer !== null) {
        areas.push(times(w, constant('footer')));
        rows.push('auto');
        elements.push(<GridLayoutElement key="footer" $area="footer">{footer}</GridLayoutElement>);
    }

    return (
        <GridLayoutWrap $areas={areas} $rows={rows.length===1?['100%']:rows} $columns={columns.length===1?['100%']:columns}>{elements}</GridLayoutWrap>
    );
}

export default GridLayout;
